"use client";

import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import * as React from "react";

import { cn } from "~/lib/utils";

/**
 * Marks the subtree that a `MenuGroupLabel` is allowed to name.
 *
 * Base UI's `Menu.GroupLabel` registers its id as the `aria-labelledby` of the
 * surrounding `Menu.Group` / `Menu.RadioGroup`, and *throws* when it renders
 * with no group in context (production error #31). Because menu popups mount
 * on click, that throw escapes to the root error boundary and replaces the
 * entire route with the crash screen — a whole-app outage caused by a misplaced
 * heading. Base UI does not export its group context, so this mirror lets our
 * own `MenuGroupLabel` detect the situation and degrade instead of crashing.
 */
const MenuGroupScopeContext = React.createContext(false);

function MenuGroup(props: MenuPrimitive.Group.Props) {
  return (
    <MenuGroupScopeContext.Provider value={true}>
      <MenuPrimitive.Group data-slot="menu-group" {...props} />
    </MenuGroupScopeContext.Provider>
  );
}

function MenuRadioGroup(props: MenuPrimitive.RadioGroup.Props) {
  return (
    <MenuGroupScopeContext.Provider value={true}>
      <MenuPrimitive.RadioGroup data-slot="menu-radio-group" {...props} />
    </MenuGroupScopeContext.Provider>
  );
}

const MENU_GROUP_LABEL_CLASS =
  "px-2 py-1.5 font-medium text-muted-foreground text-xs data-inset:ps-9 sm:data-inset:ps-8";

function MenuGroupLabel({
  className,
  inset,
  ...props
}: MenuPrimitive.GroupLabel.Props & {
  inset?: boolean;
}) {
  const isInsideGroup = React.useContext(MenuGroupScopeContext);

  // A label with no group to name has nothing to associate itself with, so the
  // accessible-name wiring is moot either way. Render the heading as a plain
  // presentational element rather than letting Base UI throw and take the whole
  // route down with it. The dev-only error keeps the mistake loud where it can
  // still be fixed, instead of trading a crash for silence.
  if (!isInsideGroup) {
    if (import.meta.env.DEV) {
      console.error(
        "[ui/menu] <MenuGroupLabel> was rendered outside <MenuGroup>/<MenuRadioGroup>. " +
          "Move the label inside the group it names so it becomes that group's accessible name. " +
          "Rendering an unassociated label instead.",
      );
    }
    // `render` and the function forms of `style`/`children` are Base UI
    // state-driven APIs with no meaning once the primitive is out of the
    // picture, so they are dropped rather than forwarded to a plain element.
    const { render: _render, style, children, ...rest } = props;
    return (
      <div
        className={cn(MENU_GROUP_LABEL_CLASS, className)}
        data-inset={inset}
        data-slot="menu-label"
        role="presentation"
        style={typeof style === "function" ? undefined : style}
        {...rest}
      >
        {typeof children === "function" ? null : children}
      </div>
    );
  }

  return (
    <MenuPrimitive.GroupLabel
      className={cn(MENU_GROUP_LABEL_CLASS, className)}
      data-inset={inset}
      data-slot="menu-label"
      {...props}
    />
  );
}

export { MenuGroup, MenuGroupLabel, MenuRadioGroup };
